import {
  createEffect,
  createEvent,
  createStore,
  sample,
  combine,
} from 'effector';
import { updateCameraConfig } from './appConfig';
import type { Sight, SightId, CameraModeConfig } from '../types';
import type { AssistantMessage } from '../types/assistant';
import type { SceneBindings } from '../scene/createScene';
import { sceneLoadingReset } from './sceneLoading';

export type VirtualTourStep = AssistantMessage;

export type ApplicationState = {
  sceneId: string | number | null;
  sights: Record<SightId, Sight>;
  orderedSightIds: SightId[];
  state:
    | {
        type: 'root';
      }
    | {
        type: 'free-camera';
      }
    | {
        type: 'virtual-tour';
        paused: boolean;
        currentStep: number;
        sightId: SightId | null;
      }
    | {
        type: 'sight-details';
        sightId: SightId | null;
      };
  virtualTour: VirtualTourStep[];
};

export interface NavigationOptions {
  smooth?: boolean;
  currentTime?: number;
}

export type SightSelectionSource = 'poi' | 'assistant';

export type SightSelectionPayload = {
  id: SightId;
  source?: SightSelectionSource;
} & NavigationOptions;

const initialState: ApplicationState = {
  sceneId: null,
  sights: {},
  orderedSightIds: [],
  state: {
    type: 'free-camera',
  },
  virtualTour: [],
};

export const setSceneId = createEvent<string | number | null>();
export const setSights = createEvent<Record<SightId, Sight>>();
export const setOrderedSightIds = createEvent<SightId[]>();
export const setVirtualTour = createEvent<VirtualTourStep[]>();
export const stopVirtualTour = createEvent();
export const sceneReady = createEvent<SceneBindings>();

const applyVirtualTourStep = createEvent<
  { index: number; paused?: boolean } & Required<Pick<NavigationOptions, 'smooth'>> &
    Pick<NavigationOptions, 'currentTime'>
>();
export const goToVirtualTourStep = createEvent<
  { index: number; paused?: boolean } & NavigationOptions
>();
export const goToVirtualTourSight = createEvent<
  { id: SightId; paused?: boolean } & NavigationOptions
>();
export const goToSight = createEvent<{ id: SightId } & NavigationOptions>();
export const userSelectedSight = createEvent<SightSelectionPayload>();
export const clearFocusedSight = createEvent();
export const clearOrbitTargetOverride = createEvent();
export const requestClearOrbitTargetOverride = createEvent();
export const nextVirtualTourStep = createEvent<NavigationOptions | void>();
export const previousVirtualTourStep = createEvent<NavigationOptions | void>();
export const firstVirtualTourStep = createEvent<NavigationOptions | void>();
export const lastVirtualTourStep = createEvent<NavigationOptions | void>();
export const continueVirtualTour = createEvent<NavigationOptions | void>();
export const setOrbitAnimationEnabled = createEvent<boolean>();
export const userInteracted = createEvent();
export const updateSightCameraConfig = createEvent<{
  sightId: SightId;
  cameraConfig: Partial<CameraModeConfig>;
}>();

export const $appState = createStore<ApplicationState>(initialState)
  .on(setSceneId, (state, sceneId) => ({ ...state, sceneId }))
  .on(setSights, (state, sights) => ({ ...state, sights }))
  .on(setOrderedSightIds, (state, orderedSightIds) => ({
    ...state,
    orderedSightIds,
  }))
  .on(setVirtualTour, (state, virtualTour) => ({ ...state, virtualTour }))
  .on(stopVirtualTour, (state) => ({
    ...state,
    virtualTour: [],
    state: {
      type: 'free-camera',
    },
  }))
  .on(applyVirtualTourStep, (state, payload) => {
    const nextStep = state.virtualTour[payload.index];
    if (!nextStep) {
      return state;
    }
    const nextSightId = nextStep.actions.find(
      (action) => action.type === 'focus-sight'
    )?.sightId ?? null;
    const pausedState =
      payload.paused ??
      (state.state.type === 'virtual-tour' ? state.state.paused : false);
    return {
      ...state,
      state: {
        type: 'virtual-tour',
        paused: pausedState,
        currentStep: payload.index,
        sightId: nextSightId,
      },
    };
  })
  .on(updateSightCameraConfig, (state, { sightId, cameraConfig }) => {
    const sight = state.sights[sightId];
    if (!sight) return state;
    return {
      ...state,
      sights: {
        ...state.sights,
        [sightId]: {
          ...sight,
          cameraConfig: { ...sight.cameraConfig, ...cameraConfig },
        },
      },
    };
  });

export const $virtualTourSteps = $appState.map((state) => state.virtualTour);
export const $sightsStore = $appState.map((state) => state.sights);
export const $orderedSightIds = $appState.map((state) => state.orderedSightIds);
export const $sceneId = $appState.map((state) => state.sceneId);

export const $sceneSights = combine(
  $sightsStore,
  $sceneId,
  (sights, sceneId) => {
    if (sceneId === null) return sights;
    const filtered: Record<SightId, Sight> = {};
    for (const [id, sight] of Object.entries(sights)) {
      if (sight.sceneId === sceneId) {
        filtered[id as SightId] = sight;
      }
    }
    return filtered;
  }
);

export const $sceneOrderedSightIds = combine(
  $orderedSightIds,
  $sightsStore,
  $sceneId,
  (ids, sights, sceneId) => {
    if (sceneId === null) return ids;
    return ids.filter((id) => sights[id]?.sceneId === sceneId);
  }
);
const $focusedSightId = createStore<SightId | null>(null)
  .reset(setSights)
  .reset(sceneLoadingReset)
  .on(goToSight, (_, payload) => payload.id)
  .on(clearFocusedSight, () => null);
export const $totalVirtualTourSteps = $virtualTourSteps.map(
  (steps) => steps.length
);
export const $isVirtualTourActive = $appState.map(
  (state) => state.state.type === 'virtual-tour'
);
export const $isVirtualTourPaused = $appState.map((state) =>
  state.state.type === 'virtual-tour' ? state.state.paused : false
);
export const $currentVirtualTourIndex = $appState.map((state) =>
  state.state.type === 'virtual-tour' ? state.state.currentStep : -1
);
export const $currentVirtualTourStep = combine(
  $virtualTourSteps,
  $currentVirtualTourIndex,
  (steps, index) => (index >= 0 && index < steps.length ? steps[index] : null)
);

export const $currentSight = combine($sightsStore, $focusedSightId, (sights, sightId) =>
  sightId ? sights[sightId] ?? null : null
);

export const $sceneBindings = createStore<SceneBindings | null>(null)
  .on(sceneReady, (_, ctx) => ctx)
  .reset(sceneLoadingReset);
export const $orbitAnimationEnabled = createStore(false)
  .on(setOrbitAnimationEnabled, (_, enabled) => enabled)
  .on(goToSight, () => false);

const $pendingOrbitTargetOverrideClear = createStore(false)
  .on(requestClearOrbitTargetOverride, () => true)
  .reset(clearOrbitTargetOverride);

$appState.on(userInteracted, (state) => {
  if (state.state.type !== 'virtual-tour' || state.state.paused) {
    return state;
  }
  return {
    ...state,
    state: {
      ...state.state,
      paused: true,
    },
  };
});

class NavigationCancelledError extends Error {
  constructor() {
    super('Navigation cancelled');
    this.name = 'NavigationCancelledError';
  }
}

let cancelOngoingNavigation: (() => void) | null = null;

// CRITICAL (iOS OOM fix): on scene teardown (return to map / scene switch),
// drop the module-level closure that holds a strong reference to the current
// `scene: SceneBindings` inside `cancelCurrentNavigation`. Without this,
// even after `$sceneBindings` is reset, the scene graph is still retained
// via this closure and prevents JSC from collecting it on iOS.
sceneLoadingReset.watch(() => {
  if (cancelOngoingNavigation) {
    try {
      cancelOngoingNavigation();
    } catch {
      /* already-settled navigations throw; ignore */
    }
    cancelOngoingNavigation = null;
  }
});

const navigateFx = createEffect<
  {
    scene: SceneBindings;
    sight: Sight;
    index: number;
    smooth: boolean;
    currentTime?: number;
  },
  { sight: Sight; index: number }
>(({ scene, sight, index, smooth, currentTime }) => {
  if (cancelOngoingNavigation) {
    cancelOngoingNavigation();
    cancelOngoingNavigation = null;
  }

  return new Promise<{ sight: Sight; index: number }>((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      settled = true;
      if (cancelOngoingNavigation === cancelCurrentNavigation) {
        cancelOngoingNavigation = null;
      }
    };

    const cancelCurrentNavigation = () => {
      if (settled) {
        return;
      }
      cleanup();
      scene.sightManager.cancelTransition();
      scene.tellerAssistant?.setTransitionMode(false);
      reject(new NavigationCancelledError());
    };

    cancelOngoingNavigation = cancelCurrentNavigation;

    if (smooth) {
      // Enable transition mode: ball leads the camera (appears to "pull" towards POI)
      scene.tellerAssistant?.setTransitionMode(true);
      scene.sightManager.transitionToSight(
        sight,
        undefined,
        () => {
          if (settled) {
            return;
          }
          // Disable transition mode: ball returns to normal lag behavior
          scene.tellerAssistant?.setTransitionMode(false);
          cleanup();
          resolve({ sight, index });
        },
        currentTime
      );
      return;
    }

    cleanup();
    scene.sightManager.moveToSight(sight);
    resolve({ sight, index });
  });
});

export const $currentSightIndex = createStore(-1)
  .on(navigateFx.doneData, (_, { index }) => index)
  .reset(setVirtualTour);

$focusedSightId.on(navigateFx.doneData, (_, { sight }) => sight.id);

export const $isSceneReady = $sceneBindings.map(
  (bindings) => bindings !== null
);
export const $isNavigating = navigateFx.pending;
export const $canNavigate = combine(
  $sceneBindings,
  $totalVirtualTourSteps,
  (scene, total) => Boolean(scene) && total > 0
);

const smoothFromOptions = (options?: NavigationOptions | void) =>
  options?.smooth ?? true;

sample({
  clock: userSelectedSight,
  target: goToSight,
});

sample({
  clock: goToSight,
  source: { scene: $sceneBindings, sights: $sightsStore },
  filter: ({ scene, sights }, payload) => Boolean(scene && sights[payload.id]),
  fn: ({ scene, sights }, payload) => ({
    scene: scene!,
    sight: sights[payload.id],
    index: -1,
    smooth: smoothFromOptions(payload),
    currentTime: payload.currentTime,
  }),
  target: navigateFx,
});

sample({
  clock: userSelectedSight,
  fn: () => ({ orbitAutoMove: false }),
  target: updateCameraConfig,
});

sample({
  clock: goToVirtualTourSight,
  fn: () => ({ orbitAutoMove: false }),
  target: updateCameraConfig,
});

sample({
  clock: goToVirtualTourStep,
  fn: () => ({ orbitAutoMove: false }),
  target: updateCameraConfig,
});

sample({
  clock: setVirtualTour,
  fn: () => ({ orbitAutoMove: false }),
  target: updateCameraConfig,
});

sample({
  clock: stopVirtualTour,
  fn: () => false,
  target: setOrbitAnimationEnabled,
});


sample({
  clock: goToVirtualTourSight,
  source: $appState,
  filter: (state, payload) =>
    state.virtualTour.some((step) =>
      step.actions.some(
        (action) => action.type === 'focus-sight' && action.sightId === payload.id
      )
    ),
  fn: (state, payload) => {
    const index = state.virtualTour.findIndex(
      (step) =>
        step.actions.some(
          (action) =>
            action.type === 'focus-sight' && action.sightId === payload.id
        )
    );
    return {
      index,
      smooth: smoothFromOptions(payload),
      currentTime: payload.currentTime,
      paused: payload.paused,
    };
  },
  target: applyVirtualTourStep,
});

sample({
  clock: goToVirtualTourStep,
  source: $totalVirtualTourSteps,
  filter: (total, payload) =>
    total > 0 && payload.index >= 0 && payload.index < total,
  fn: (_, payload) => ({
    index: payload.index,
    smooth: smoothFromOptions(payload),
    currentTime: payload.currentTime,
    paused: payload.paused,
  }),
  target: applyVirtualTourStep,
});

sample({
  clock: nextVirtualTourStep,
  source: {
    index: $currentVirtualTourIndex,
    total: $totalVirtualTourSteps,
  },
  filter: ({ total, index }) => total > 0 && index < total - 1,
  fn: ({ index }, payload) => {
    const nextIndex = index >= 0 ? index + 1 : 0;
    return { index: nextIndex, smooth: smoothFromOptions(payload) };
  },
  target: applyVirtualTourStep,
});

sample({
  clock: continueVirtualTour,
  source: {
    index: $currentVirtualTourIndex,
    total: $totalVirtualTourSteps,
  },
  filter: ({ total, index }) => total > 0 && index < total - 1,
  fn: ({ index }, payload) => {
    const nextIndex = index >= 0 ? index + 1 : 0;
    return {
      index: nextIndex,
      smooth: smoothFromOptions(payload),
      paused: false,
    };
  },
  target: applyVirtualTourStep,
});

sample({
  clock: previousVirtualTourStep,
  source: {
    index: $currentVirtualTourIndex,
    total: $totalVirtualTourSteps,
  },
  filter: ({ total, index }) => total > 0 && index > 0,
  fn: ({ index }, payload) => {
    const prevIndex = index - 1;
    return { index: prevIndex, smooth: smoothFromOptions(payload) };
  },
  target: applyVirtualTourStep,
});

sample({
  clock: firstVirtualTourStep,
  source: $totalVirtualTourSteps,
  filter: (total) => total > 0,
  fn: (_, payload) => ({ index: 0, smooth: smoothFromOptions(payload) }),
  target: applyVirtualTourStep,
});

sample({
  clock: lastVirtualTourStep,
  source: $totalVirtualTourSteps,
  filter: (total) => total > 0,
  fn: (total, payload) => ({
    index: total - 1,
    smooth: smoothFromOptions(payload),
  }),
  target: applyVirtualTourStep,
});

sample({
  clock: setVirtualTour,
  filter: (steps) => steps.length > 0,
  fn: () => ({ index: 0, smooth: true }),
  target: applyVirtualTourStep,
});

sample({
  clock: sceneReady,
  source: {
    index: $currentVirtualTourIndex,
    total: $totalVirtualTourSteps,
  },
  filter: ({ total }) => total > 0,
  fn: ({ index }) => ({
    index: index >= 0 ? index : 0,
    smooth: false,
  }),
  target: applyVirtualTourStep,
});


sample({
  clock: applyVirtualTourStep,
  source: { scene: $sceneBindings, state: $appState },
  filter: ({ scene, state }, payload) => {
    if (!scene) {
      return false;
    }
    const step = state.virtualTour[payload.index];
    const sightId =
      step?.actions.find((action) => action.type === 'focus-sight')?.sightId ??
      null;
    return Boolean(step && sightId && state.sights[sightId]);
  },
  fn: ({ scene, state }, payload) => {
    const step = state.virtualTour[payload.index];
    const sightId =
      step.actions.find((action) => action.type === 'focus-sight')?.sightId ??
      null;
    const sight = sightId ? state.sights[sightId] : null;
    return {
      scene: scene!,
      sight: sight!,
      index: payload.index,
      smooth: payload.smooth,
      currentTime: payload.currentTime,
    };
  },
  target: navigateFx,
});

sample({
  clock: applyVirtualTourStep,
  source: $appState,
  filter: (state, payload) => {
    const step = state.virtualTour[payload.index];
    const sightId =
      step?.actions.find((action) => action.type === 'focus-sight')?.sightId ??
      null;
    return Boolean(sightId && state.sights[sightId]);
  },
  fn: (state, payload) => {
    const step = state.virtualTour[payload.index];
    return (
      step?.actions.find((action) => action.type === 'focus-sight')?.sightId ??
      null
    );
  },
  target: $focusedSightId,
});

sample({
  clock: applyVirtualTourStep,
  source: $appState,
  filter: (state, payload) => {
    const step = state.virtualTour[payload.index];
    const sightId =
      step?.actions.find((action) => action.type === 'focus-sight')?.sightId ??
      null;
    return Boolean(sightId && state.sights[sightId]);
  },
  fn: () => true,
  target: setOrbitAnimationEnabled,
});

sample({
  clock: navigateFx.done,
  source: $pendingOrbitTargetOverrideClear,
  filter: (pending) => pending,
  fn: () => undefined,
  target: clearOrbitTargetOverride,
});

const IDLE_ORBIT_TIMEOUT_MS = 5000;
let idleOrbitTimeout: ReturnType<typeof setTimeout> | null = null;
let idleOrbitSightId: SightId | null = null;

const clearIdleOrbitTimeout = () => {
  if (idleOrbitTimeout) {
    clearTimeout(idleOrbitTimeout);
    idleOrbitTimeout = null;
  }
  idleOrbitSightId = null;
};

const scheduleIdleOrbit = (sightId: SightId) => {
  clearIdleOrbitTimeout();
  idleOrbitSightId = sightId;
  idleOrbitTimeout = setTimeout(() => {
    idleOrbitTimeout = null;
    const currentSight = $currentSight.getState();
    if (
      !$isVirtualTourActive.getState() &&
      !$orbitAnimationEnabled.getState() &&
      currentSight &&
      currentSight.id === sightId &&
      idleOrbitSightId === sightId
    ) {
      setOrbitAnimationEnabled(true);
    }
  }, IDLE_ORBIT_TIMEOUT_MS);
};

// userSelectedSight.watch(({ id }) => {
//   if ($isVirtualTourActive.getState()) {
//     return;
//   }
//   scheduleIdleOrbit(id);
// });

userInteracted.watch(() => {
  clearIdleOrbitTimeout();
});

clearFocusedSight.watch(() => {
  clearIdleOrbitTimeout();
});

stopVirtualTour.watch(() => {
  clearIdleOrbitTimeout();
});

setVirtualTour.watch(() => {
  clearIdleOrbitTimeout();
});

goToVirtualTourStep.watch(() => {
  clearIdleOrbitTimeout();
});

goToVirtualTourSight.watch(() => {
  clearIdleOrbitTimeout();
});

setOrbitAnimationEnabled.watch((enabled) => {
  if (enabled) {
    clearIdleOrbitTimeout();
  }
});
